import { type KeyboardEvent, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { useAssessmentSession } from "../../contexts/assessmentUserContext";
import {
  formatScopeLabels,
  useBenchmarksDashboard,
  type BenchmarkDashboardRow,
} from "../../hooks/useBenchmarksDashboard";
import { getApiUrls } from "../../lib/apiUrl";
import { deleteAssessmentBenchmarkRequest } from "../../services/assessmentContentService";

const BENCHMARK_TYPE_LABELS: Record<string, string> = {
  boy: "BOY",
  moy: "MOY",
  eoy: "EOY",
};

const SKILL_LABELS: Record<string, string> = {
  reading: "Reading",
  listening: "Listening",
  speaking: "Speaking",
  writing: "Writing",
};

const formatBenchmarkType = (value: string): string =>
  BENCHMARK_TYPE_LABELS[value] ?? value;

const formatSkill = (value: string): string => SKILL_LABELS[value] ?? value;

const formatBenchmarkDate = (value?: string | null): string => {
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

type BenchmarksDashboardLocationState = {
  benchmarkMessage?: string;
};

const SUCCESS_MESSAGE_AUTO_DISMISS_MS = 5000;

const formatDateRange = (
  start?: string | null,
  end?: string | null,
): string => {
  const startLabel = formatBenchmarkDate(start);
  const endLabel = formatBenchmarkDate(end);

  if (startLabel && endLabel) {
    return `${startLabel} - ${endLabel}`;
  }
  if (startLabel) {
    return startLabel;
  }
  if (endLabel) {
    return endLabel;
  }
  return "-";
};

const getBenchmarkTestFormsPath = (benchmarkId: string): string =>
  `/assessments-admin/benchmarks/manage?benchmark=${encodeURIComponent(benchmarkId)}`;

export function BenchmarksDashboardPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = useAssessmentSession();
  const { assessmentsApiUrl: apiUrl } = getApiUrls();
  const { rows, isLoading, error, reload } = useBenchmarksDashboard();
  const [benchmarkMessage, setBenchmarkMessage] = useState<string | null>(
    () =>
      (location.state as BenchmarksDashboardLocationState | null)
        ?.benchmarkMessage ?? null,
  );
  const [titleSearch, setTitleSearch] = useState("");
  const [benchmarkToDelete, setBenchmarkToDelete] =
    useState<BenchmarkDashboardRow | null>(null);
  const [deletingBenchmarkId, setDeletingBenchmarkId] = useState<string | null>(
    null,
  );
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (
      !(location.state as BenchmarksDashboardLocationState | null)
        ?.benchmarkMessage
    ) {
      return;
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (!benchmarkMessage) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setBenchmarkMessage(null);
    }, SUCCESS_MESSAGE_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timeoutId);
  }, [benchmarkMessage]);

  const filteredRows = useMemo(() => {
    const query = titleSearch.trim().toLowerCase();
    if (!query) {
      return rows;
    }
    return rows.filter((row) => row.title.toLowerCase().includes(query));
  }, [rows, titleSearch]);

  const handleDeleteBenchmark = async () => {
    if (!benchmarkToDelete || !token || !apiUrl) {
      return;
    }

    try {
      setDeletingBenchmarkId(benchmarkToDelete.id);
      setActionError(null);
      await deleteAssessmentBenchmarkRequest({
        apiUrl,
        token,
        id: benchmarkToDelete.id,
      });
      setBenchmarkToDelete(null);
      await reload();
    } catch (deleteError: unknown) {
      setActionError(
        deleteError instanceof Error
          ? deleteError.message
          : "Unable to delete assessment benchmark.",
      );
    } finally {
      setDeletingBenchmarkId(null);
    }
  };

  const handleBenchmarkRowKeyDown = (
    event: KeyboardEvent<HTMLTableRowElement>,
    benchmarkId: string,
  ) => {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }
    event.preventDefault();
    navigate(getBenchmarkTestFormsPath(benchmarkId));
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          position: "relative",
          flexShrink: 0,
          pt: 2,
          pb: 1.5,
        }}
      >
        <Typography variant="h4" component="h1">
          Benchmarks
        </Typography>
        <Stack direction="row" spacing={2} alignItems={"center"}>
          <Link
            component={RouterLink}
            to="/assessments-admin/benchmarks/manage?benchmark=reading-demo&is_demo=true"
          >
            Reading Demo
          </Link>
          <Link
            component={RouterLink}
            to="/assessments-admin/benchmarks/manage?benchmark=listening-demo&is_demo=true"
          >
            Listening Demo
          </Link>
          <Button
            component={RouterLink}
            to="/assessments-admin/benchmarks/create"
            variant="contained"
            size="small"
          >
            Add Benchmark
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pb: 4 }}>
        {benchmarkMessage ? (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setBenchmarkMessage(null)}
          >
            {benchmarkMessage}
          </Alert>
        ) : null}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <TextField
            label="Search by benchmark title"
            value={titleSearch}
            onChange={(event) => setTitleSearch(event.target.value)}
            size="small"
            fullWidth
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Showing {filteredRows.length} of {rows.length} benchmark(s)
          </Typography>
        </Paper>

        {isLoading ? (
          <Typography variant="body1" sx={{ mb: 2 }}>
            Loading benchmarks...
          </Typography>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        {actionError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        ) : null}
        {!isLoading && !error ? (
          <Paper variant="outlined">
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ "& .MuiTableCell-root": { fontWeight: 700 } }}>
                  <TableRow>
                    <TableCell>Skill</TableCell>
                    <TableCell>Benchmark Title</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>District</TableCell>
                    <TableCell>School</TableCell>
                    <TableCell>Grade</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Box sx={{ py: 2 }}>
                          <Typography color="text.secondary">
                            No benchmarks found.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRows.map((row) => (
                      <TableRow
                        key={row.id}
                        hover
                        tabIndex={0}
                        role="button"
                        aria-label={`View test forms for benchmark ${row.title}`}
                        onClick={() =>
                          navigate(getBenchmarkTestFormsPath(row.id))
                        }
                        onKeyDown={(event) =>
                          handleBenchmarkRowKeyDown(event, row.id)
                        }
                        sx={{
                          cursor: "pointer",
                          "&:focus-visible": {
                            outline: "2px solid",
                            outlineColor: "primary.main",
                            outlineOffset: -2,
                          },
                        }}
                      >
                        <TableCell>{formatSkill(row.skill)}</TableCell>
                        <TableCell>{row.title}</TableCell>
                        <TableCell>
                          {formatBenchmarkType(row.benchmark_type)}
                        </TableCell>
                        <TableCell>
                          {formatDateRange(row.start_at, row.close_at)}
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            arrow
                            placement="right"
                            title={
                              row.districtNames.length > 0 ? (
                                <Box>
                                  {row.districtNames.map(
                                    (districtName, index) => (
                                      <Box
                                        key={`${row.district_ids[index]}-${districtName}`}
                                        sx={{ lineHeight: 1.3 }}
                                      >
                                        {districtName}
                                      </Box>
                                    ),
                                  )}
                                </Box>
                              ) : (
                                ""
                              )
                            }
                          >
                            <Box component="span">
                              {formatScopeLabels(row.districtNames, "district")}
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{row.schoolNames}</TableCell>
                        <TableCell>{row.gradeNames}</TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            alignItems="center"
                          >
                            <IconButton
                              component={RouterLink}
                              to={getBenchmarkTestFormsPath(row.id)}
                              aria-label={`View benchmark ${row.title}`}
                              color="primary"
                              size="small"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              component={RouterLink}
                              to={`/assessments-admin/benchmarks/${encodeURIComponent(row.id)}/edit`}
                              aria-label={`Edit benchmark ${row.title}`}
                              color="primary"
                              size="small"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              aria-label={`Delete benchmark ${row.title}`}
                              color="error"
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                setActionError(null);
                                setBenchmarkToDelete(row);
                              }}
                              disabled={deletingBenchmarkId === row.id}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : null}
      </Box>
      <ConfirmationDialog
        open={Boolean(benchmarkToDelete)}
        title="Delete benchmark?"
        description={`Delete "${benchmarkToDelete?.title.trim() || "this benchmark"}"? This will soft-delete the benchmark and remove it from active admin flows.`}
        confirmLabel="Delete Benchmark"
        confirmColor="error"
        isConfirming={Boolean(
          benchmarkToDelete && deletingBenchmarkId === benchmarkToDelete.id,
        )}
        onClose={() => {
          if (!deletingBenchmarkId) {
            setBenchmarkToDelete(null);
          }
        }}
        onConfirm={handleDeleteBenchmark}
      />
    </Container>
  );
}
